import React, { useState } from 'react';
import { Upload, Button, Table, message, Card, Alert, Space } from 'antd';
import { UploadOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

function UserImport() {
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [checking, setChecking] = useState(false);

  // 预览
  const handlePreview = async () => {
    if (fileList.length === 0) {
      message.warning('请先选择Excel文件');
      return;
    }

    setChecking(true);
    setPreviewData(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', fileList[0]);

    try {
      const res = await api.post('/users/batch/import-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.code === 200) {
        setPreviewData(res.data);
        const totalDup = (res.data.existing_duplicates?.length || 0) + (res.data.internal_duplicates?.length || 0);
        if (totalDup > 0) {
          message.warning(`发现 ${totalDup} 个重复用户名`);
        } else {
          message.success('预览完成，未发现重复数据');
        }
      }
    } catch (err) {
      // 错误已在拦截器中处理
    }
    setChecking(false);
  };

  const handleImport = async (skipDuplicates = false) => {
    if (fileList.length === 0) {
      message.warning('请先选择Excel文件');
      return;
    }

    setImporting(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', fileList[0]);
    if (skipDuplicates) {
      formData.append('skip_duplicates', 'true');
    }

    try {
      const res = await api.post('/users/batch/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.code === 200) {
        setResult(res.data);
        message.success('导入完成');
      }
    } catch (err) {
      // 错误已在拦截器中处理
    }
    setImporting(false);
  };

  const hasDuplicates = previewData && (
    (previewData.existing_duplicates && previewData.existing_duplicates.length > 0) ||
    (previewData.internal_duplicates && previewData.internal_duplicates.length > 0)
  );

  const errorColumns = [
    { title: '行号', dataIndex: 'row', width: 80 },
    { title: '错误信息', dataIndex: 'error' }
  ];

  const previewColumns = [
    { title: '用户名', dataIndex: 'username' },
    { title: '显示名称', dataIndex: 'name' },
    { title: '角色', dataIndex: 'role', width: 100, render: v => v === 'supplier' ? '主体' : '管理员' },
    { title: '关联主体', dataIndex: 'supplier_name', render: v => v || '-' },
    { title: '状态', dataIndex: 'status', width: 80, render: v => v === '0' ? '禁用' : '启用' }
  ];

  return (
    <div>
      <h3>批量导入用户</h3>

      <Card style={{ marginTop: 16 }}>
        <Alert
          message="导入说明"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>仅超管可执行批量导入用户操作</li>
              <li>支持 .xlsx 和 .xls 格式</li>
              <li>第一行为表头，以下为数据行</li>
              <li>用户名、密码、显示名称为必填项</li>
              <li>角色填写「管理员」或「supplier」，默认为管理员</li>
              <li>主体角色需填写主体名称（需已存在）</li>
              <li>权限列填写 0 或 1，1=允许（默认），0=禁止</li>
              <li>新导入的管理员默认拥有全部菜单权限</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Card title="字段映射" size="small" style={{ marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 13 }}>
            <span style={{ color: '#ff4d4f' }}>用户名 → 用户名（必填）</span>
            <span style={{ color: '#ff4d4f' }}>密码 → 密码（必填）</span>
            <span style={{ color: '#ff4d4f' }}>显示名称 → 显示名称（必填）</span>
            <span>角色 → 角色（admin/supplier）</span>
            <span>主体名称 → 主体名称</span>
            <span>新增权限 → 新增权限（0/1）</span>
            <span>删除权限 → 删除权限（0/1）</span>
            <span>导入权限 → 导入权限（0/1）</span>
            <span>快速导入权限 → 快速导入权限（0/1）</span>
            <span>状态 → 状态（0=禁用/1=启用）</span>
          </div>
        </Card>

        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={(file) => {
              setFileList([file]);
              setPreviewData(null);
              setResult(null);
              return false;
            }}
            fileList={fileList}
            onRemove={() => { setFileList([]); setPreviewData(null); setResult(null); }}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>选择Excel文件</Button>
          </Upload>

          <Space>
            <Button
              onClick={handlePreview}
              loading={checking}
              disabled={fileList.length === 0}
              size="large"
            >
              预览
            </Button>
            {hasDuplicates ? (
              <>
                <Button
                  onClick={() => handleImport(true)}
                  loading={importing}
                  size="large"
                >
                  去除重复后导入
                </Button>
                <Button
                  type="primary"
                  onClick={() => handleImport(false)}
                  loading={importing}
                  size="large"
                >
                  全部导入（含重复）
                </Button>
              </>
            ) : (
              <Button
                type="primary"
                onClick={() => handleImport(false)}
                loading={importing}
                disabled={fileList.length === 0}
                size="large"
              >
                开始导入
              </Button>
            )}
            <Button onClick={() => navigate('/users')}>返回列表</Button>
          </Space>
        </Space>
      </Card>

      {/* 预览结果 */}
      {previewData && (
        <Card title="数据预览" style={{ marginTop: 16 }}>
          <Space size="large" style={{ marginBottom: 16 }}>
            <span>总行数: {previewData.total}</span>
            {previewData.existing_duplicates && previewData.existing_duplicates.length > 0 && (
              <span style={{ color: '#ff4d4f' }}>数据库已存在: {previewData.existing_duplicates.length} 个</span>
            )}
            {previewData.internal_duplicates && previewData.internal_duplicates.length > 0 && (
              <span style={{ color: '#faad14' }}>Excel内部重复: {previewData.internal_duplicates.length} 个</span>
            )}
          </Space>

          {previewData.existing_duplicates && previewData.existing_duplicates.length > 0 && (
            <Alert
              message="以下用户名已存在于数据库中"
              description={previewData.existing_duplicates.join('、')}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {previewData.internal_duplicates && previewData.internal_duplicates.length > 0 && (
            <Alert
              message="Excel中存在重复用户名"
              description={previewData.internal_duplicates.join('、')}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {previewData.preview && previewData.preview.length > 0 && (
            <Table
              columns={previewColumns}
              dataSource={previewData.preview.map((r, i) => ({ ...r, key: i }))}
              pagination={{ pageSize: 20 }}
              size="small"
              bordered
            />
          )}
        </Card>
      )}

      {/* 导入结果 */}
      {result && (
        <Card title="导入结果" style={{ marginTop: 16 }}>
          <Space size="large" style={{ marginBottom: 16 }}>
            <span style={{ color: '#52c41a', fontSize: 16 }}>
              <CheckCircleOutlined /> 成功: {result.success} 条
            </span>
            {result.skip > 0 && (
              <span style={{ color: '#faad14', fontSize: 16 }}>
                跳过重复: {result.skip} 条
              </span>
            )}
            {result.error > 0 && (
              <span style={{ color: '#ff4d4f', fontSize: 16 }}>
                <CloseCircleOutlined /> 失败: {result.error} 条
              </span>
            )}
          </Space>

          {result.errors && result.errors.length > 0 && (
            <Table
              columns={errorColumns}
              dataSource={result.errors.map((e, i) => ({ ...e, key: i }))}
              pagination={false}
              size="small"
              bordered
            />
          )}

          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => navigate('/users')}>
              查看用户列表
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

export default UserImport;
